import { useState, useEffect, useCallback } from 'react';
import { getLocale, isRtl, useIntl } from '@edx/frontend-platform/i18n';
import { DataTable } from '@openedx/paragon';
import { getSubsectionGradeSnapshot } from '../../../data/api';
import { useContextId } from '../../../../data/hooks';

import { useModel } from '../../../../generic/model-store';
import messages from '../messages';
import SubsectionTitleCell from './SubsectionTitleCell';
import LockedGradeCell from './LockedGradeCell';
import { showUngradedAssignments } from '../../utils';

const DetailedGradesTable = () => {
  const intl = useIntl();
  const courseId = useContextId();
  const [snapshotDataMap, setSnapshotDataMap] = useState({});
  const [snapshotFailureMap, setSnapshotFailureMap] = useState({});

  const {
    sectionScores,
  } = useModel('progress', courseId);

  // Get list of subsections with grade snapshots
  const snapshotSubsectionsModel = useModel('gradeSnapshotSubsections', courseId);
  const snapshotSubsectionIds = snapshotSubsectionsModel?.subsectionIds || [];

  const isLocaleRtl = isRtl(getLocale());

  const fetchSnapshotForSubsection = useCallback(async (subsectionId) => {
    if (!subsectionId || snapshotDataMap[subsectionId] || snapshotFailureMap[subsectionId]) {
      return;
    }

    try {
      const data = await getSubsectionGradeSnapshot(courseId, subsectionId);
      if (!Array.isArray(data) || data.length === 0) {
        // eslint-disable-next-line no-console
        console.error(`Grade snapshot for subsection ${subsectionId} returned empty or invalid data`);
        setSnapshotFailureMap(prev => ({
          ...prev,
          [subsectionId]: true,
        }));
        return;
      }

      setSnapshotDataMap(prev => ({
        ...prev,
        [subsectionId]: data,
      }));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Failed to fetch grade snapshot for subsection ${subsectionId}:`, error);
      setSnapshotFailureMap(prev => ({
        ...prev,
        [subsectionId]: true,
      }));
    }
  }, [courseId, snapshotDataMap, snapshotFailureMap]);

  useEffect(() => {
    if (snapshotSubsectionIds.length > 0) {
      snapshotSubsectionIds.forEach((subsectionId) => {
        fetchSnapshotForSubsection(subsectionId);
      });
    }
  }, [snapshotSubsectionIds, fetchSnapshotForSubsection]);

  return (
    sectionScores.map((chapter) => {
      const subsectionScores = chapter.subsections.filter(
        (subsection) => !!(
          (showUngradedAssignments() || subsection.hasGradedAssignment)
            && subsection.showGrades
            && (subsection.numPointsPossible > 0 || subsection.numPointsEarned > 0)
        ),
      );

      if (subsectionScores.length === 0) {
        return null;
      }

      const detailedGradesData = subsectionScores.map((subsection) => {
        // Extract bare subsection ID from blockKey (e.g., "block-v1:..." -> "abc123")
        const subsectionId = subsection.blockKey?.split('+block@')?.pop();
        const hasGradeSnapshot = snapshotSubsectionIds.includes(subsectionId)
          && !snapshotFailureMap[subsectionId];

        return {
          subsectionTitle: (
            <SubsectionTitleCell
              subsection={subsection}
              courseId={courseId}
              subsectionId={subsectionId}
              hasGradeSnapshot={hasGradeSnapshot}
              snapshotData={snapshotDataMap[subsectionId]}
              onFetchSnapshot={() => fetchSnapshotForSubsection(subsectionId)}
            />
          ),
          score: hasGradeSnapshot
            ? <LockedGradeCell snapshotData={snapshotDataMap[subsectionId]} />
            : <span className={subsection.learnerHasAccess ? '' : 'greyed-out'}>{subsection.numPointsEarned}{isLocaleRtl ? '\\' : '/'}{subsection.numPointsPossible}</span>,
        };
      });

      return (
        <div className="my-3" key={`${chapter.displayName}-grades-table`}>
          <DataTable
            data={detailedGradesData}
            itemCount={detailedGradesData.length}
            columns={[
              {
                Header: chapter.displayName,
                accessor: 'subsectionTitle',
                headerClassName: 'h5 mb-0',
                cellClassName: 'mw-100',
              },
              {
                Header: `${intl.formatMessage(messages.score)}`,
                accessor: 'score',
                headerClassName: 'justify-content-end h5 mb-0',
                cellClassName: 'align-top text-right small',
              },
            ]}
          >
            <DataTable.Table />
          </DataTable>
        </div>
      );
    })
  );
};

export default DetailedGradesTable;
